import { useMutation, useQuery } from "@tanstack/react-query";
import { APIs } from "../api/APIs";
import { api } from "../api";

export const useBoardItemComment = () => {
  const useGetBoardItemComments = (keys, select) => {
    const {
      boardCode,
      itemNo,
      page,
      size,
      sort,
      rawPrivateViewToken,
      useComment,
    } = keys;

    const query = { page, size, sort };

    const queryStr = new URLSearchParams(query).toString();

    const api_info = APIs.comment.list(boardCode, itemNo, queryStr);
    const headers = { ...api_info.headers };
    if (rawPrivateViewToken)
      headers["X-Post-Secret-Token"] = rawPrivateViewToken;

    return useQuery({
      queryKey: [
        "/board-item-comment?boardCode={boardCode}&itemNo={itemNo}&page=1",
        keys,
      ],
      queryFn: async () => await api({ ...api_info, headers }),
      refetchOnWindowFocus: false,
      select,
      enabled: !!boardCode && !!itemNo && useComment,
    });
  };

  const createBoardItemCommentMutation = useMutation({
    mutationFn: async (keys) => {
      const { boardCode, payload, itemNo } = keys;

      const api_info = APIs.comment.create(boardCode, itemNo, payload);
      const response = await api(api_info);
      return response.data;
    },
  });

  const updateBoardItemCommentMutation = useMutation({
    mutationFn: async (keys) => {
      const {
        boardCode,
        itemNo,
        commentNo,
        payload,
        rawPrivateViewToken,
        privateViewToken,
      } = keys;

      const api_info = APIs.comment.update(
        boardCode,
        itemNo,
        commentNo,
        payload
      );
      const headers = { ...api_info.headers };
      if (rawPrivateViewToken)
        headers["X-Post-Secret-Token"] = rawPrivateViewToken;
      if (privateViewToken)
        headers["X-Comment-Secret-Token"] = privateViewToken;

      const response = await api({ ...api_info, headers });
      return response.data;
    },
  });

  const deleteBoardItemCommentMutation = useMutation({
    mutationFn: async (keys) => {
      const {
        commentNo,
        boardCode,
        itemNo,
        rawPrivateViewToken,
        privateViewToken,
      } = keys;

      const api_info = APIs.comment.delete(boardCode, itemNo, commentNo);
      const headers = { ...api_info.headers };
      if (rawPrivateViewToken)
        headers["X-Post-Secret-Token"] = rawPrivateViewToken;
      if (privateViewToken)
        headers["X-Comment-Secret-Token"] = privateViewToken;

      const response = await api({ ...api_info, headers });
      return response.data;
    },
  });

  const verifyPasswordViewCommentMutation = useMutation({
    mutationFn: async (keys) => {
      const { boardCode, itemNo, commentNo, payload } = keys;

      const api_info = APIs.comment.verify_password_view(
        boardCode,
        itemNo,
        commentNo,
        payload
      );
      const response = await api(api_info);
      return response.data;
    },
  });

  const verifyPasswordEditCommentMutation = useMutation({
    mutationFn: async (keys) => {
      const {
        boardCode,
        itemNo,
        commentNo,
        rawPrivateViewToken,
        privateViewToken,
        payload,
      } = keys;

      const api_info = APIs.comment.verify_password_edit(
        boardCode,
        itemNo,
        commentNo,
        payload
      );
      const headers = { ...api_info.headers };
      if (rawPrivateViewToken)
        headers["X-Post-Secret-Token"] = rawPrivateViewToken;
      if (privateViewToken)
        headers["X-Comment-Secret-Token"] = privateViewToken;

      const response = await api({ ...api_info, headers });
      return response.data;
    },
  });

  const getPrivateCommentsMutation = useMutation({
    mutationFn: async (keys) => {
      const { boardCode, itemNo, rawPrivateViewToken, page, size, sort } = keys;

      const query = { page, size, sort };

      const queryStr = new URLSearchParams(query).toString();

      const api_info = APIs.comment.list(boardCode, itemNo, queryStr);

      const headers = { ...api_info.headers };
      if (rawPrivateViewToken)
        headers["X-Post-Secret-Token"] = rawPrivateViewToken;

      const response = await api({ ...api_info, headers });
      return response.data;
    },
  });

  return {
    useGetBoardItemComments,
    createBoardItemCommentMutation,
    updateBoardItemCommentMutation,
    deleteBoardItemCommentMutation,
    verifyPasswordViewCommentMutation,
    verifyPasswordEditCommentMutation,
    getPrivateCommentsMutation,
  };
};
